import { set, useForm } from "react-hook-form";
import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "@gds/core/themes/default.css";
import { updateSweepDetails, deleteSweepDetails } from '../../api/agency-profile';
import { GdkModal } from "@gdk/modal";
import { GdkButtonSwitch } from "@gdk/button-switch";
import '@gdk/button-switch/dist/styles/gdk-button-switch.min.css';
import SweepDetailsContainer from "./SweepDetailsContainer";
import '../../styles/Sweeps.css';
import { useNavigate } from "react-router-dom";
import { logDetails } from "../../api/logger-configuration";
import { UserContext } from '../../context/UserContextProvider';
import { publishSweepEvent } from "./PublishSweepEventRequestHelper";


export default function AgencyProfileForm({ expandableForm, agencyProfile, setAgencyBranches, principalAgencyProfile }) {
    const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(validationSchema)
    });
    const navigate = useNavigate();
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);
    const [resetForms, setResetForms] = useState(false);
    const gdkModal = useRef(null);
    const gdkButtonSwitch = useRef(null);
    const user = useContext(UserContext);

    useEffect(() => {
        if (!agencyProfile) return;

        const isPrincipleDeactivated = agencyProfile?.sweepDetails?.IsPrincipleDeactivated === true;
        const isSweepsPaymentMethodId = (agencyProfile?.sweepDetails?.SweepsPaymentMethodId ?? null) !== null;
        const isParentDeactivated = principalAgencyProfile?.sweepDetails?.IsPrincipleDeactivated === true;
        const isParentSweepsEnrolled = principalAgencyProfile?.sweepDetails?.IsSweepsEnrolled === true;

        setShowAlert((isPrincipleDeactivated || isParentDeactivated || !isParentSweepsEnrolled) && !isSweepsPaymentMethodId);
        setIsDisabled(isSweepsPaymentMethodId || isParentDeactivated || !isParentSweepsEnrolled);

        if (!isSweepsPaymentMethodId) {
            gdkButtonSwitch.current = new GdkButtonSwitch({
                content: `#${agencyProfile?.agencyId}`,
                initiallyChecked: !isPrincipleDeactivated && !isParentDeactivated && isParentSweepsEnrolled,
                onSwitchChange: function (buttonSwitchCheckedState) {
                    handleUnauthorizeClick(buttonSwitchCheckedState, agencyProfile);
                }
            });
        }
    }, [agencyProfile]);

    const handleUnauthorizeClick = (isDeactivated, agencyBranch) => {
        setIsDisabled(true);
        const updatedAgencyProfile = {
            ...agencyBranch,
            ivans: agencyBranch.ivans ?? {},
            sweepDetails: agencyBranch.sweepDetails ? {
                ...agencyBranch.sweepDetails,
                IsPrincipleDeactivated: !isDeactivated
            } : { IsPrincipleDeactivated: !isDeactivated }
        };

        updateSweepDetails(updatedAgencyProfile,
            () => {
                setAgencyBranches(prevBranches => prevBranches.map(branch => branch.agencyId === agencyBranch.agencyId ? updatedAgencyProfile : branch));
                setShowAlert(!isDeactivated);
                if (!isDeactivated) {
                    publishSweepEvent(updatedAgencyProfile.agencyId,
                        "BranchDisabled",
                        user?.agentId,
                        "Sweep event published successfully for branch deactivation."
                    );
                }
                else {
                    publishSweepEvent(updatedAgencyProfile.agencyId,
                        "BranchEnabled",
                        user?.agentId,
                        "Sweep event published successfully for branch activation."
                    );
                }
                setIsDisabled(false);
            },
            () => {
                setIsDisabled(false);
            }
        );
    };

    const onClickAddOrDelete = () => {
        if (agencyProfile?.sweepDetails?.SweepsPaymentMethodId) {
            setShowDeletionModal(true);
        }
        else {
            navigate("/email-settings", { state: { agencyProfile: principalAgencyProfile, branchProfile: agencyProfile, isBranch: true } });
        }
    }

    const handleDeleteClick = () => {
        setIsLoading(true);
        agencyProfile = {
            ...agencyProfile,
            ivans: agencyProfile.ivans ?? {}
        };

        deleteSweepDetails(agencyProfile,
            () => {
                const updatedProfile = {
                    ...agencyProfile
                };
                updatedProfile.sweepDetails.IsPrincipleDeactivated = true;
                updatedProfile.sweepDetails.SweepsPaymentMethodId = null;
                updatedProfile.sweepDetails.AccountNickname = null;
                updatedProfile.sweepDetails.Last4AccountNumber = null;
                updatedProfile.sweepDetails.SweepsEmail = null;
                updatedProfile.sweepDetails.AdditionalSweepEmails = [];
                updateSweepDetails(updatedProfile,
                    () => {
                        setAgencyBranches(prevBranches => prevBranches.map(branch => branch.agencyId === agencyProfile.agencyId ? updatedProfile : branch));
                        console.log("Saved agency profile.");
                        logDetails("Saved agency profile with branch sweep details deleted.");
                        // Use the helper for publishing the event
                        publishSweepEvent(
                            updatedProfile.agencyId,
                            "BranchUnEnrolled",
                            user?.agentId,
                            "Sweep event published successfully for branch unenrollment."
                        );
                        setIsDisabled(false);
                        setShowAlert(true);
                    },
                    () => {

                    }
                );
                setShowDeletionModal(false);
            },
            () => {
                setShowDeletionModal(false);
                setIsLoading(false);
            }
        );
    }

    const onCancel = () => {
        reset()
        setResetForms(prev => !prev);
    };
    useEffect(() => reset(), [reset]);

    useEffect(() => {
        gdkModal.current = new GdkModal({
            content: `#branch-deletion-modal-${agencyProfile?.agencyId}`,
            hideCloseButton: false,
            onClosed: () => {
                setShowDeletionModal(false);
            },
        });
    }, []);

    useEffect(() => {
        if (showDeletionModal === true && gdkModal.current?.show) {
            gdkModal.current.show();
        }

        if (showDeletionModal === false && gdkModal.current?.hide) {
            gdkModal.current.hide();
        }
    }, [showDeletionModal]);

    return (
        <form className="sweeps">
            <div>
                <div className="row">
                    <div className="col-sm-12">
                        {!agencyProfile?.sweepDetails?.SweepsPaymentMethodId && (
                            <><div id={`${agencyProfile?.agencyId}`} className={`button-switch-container ${isDisabled ? 'disabled' : ''}`} data-testid="button-switch-container">
                                <div className="button-switch-label-wrapper">
                                    <label htmlFor={agencyProfile?.agencyId} className="text">Agency Sweep Account
                                        <gds-tooltip>You can activate or deactivate this agency sweep account by selecting the toggle switch.</gds-tooltip>
                                    </label>
                                    <span slot="descriptive-copy">
                                        {(principalAgencyProfile?.sweepDetails?.Last4AccountNumber) ? `Account Number: ******${principalAgencyProfile?.sweepDetails?.Last4AccountNumber}` : ""}
                                    </span>
                                </div>
                                <input id={agencyProfile?.agencyId} name={agencyProfile?.agencyId} type="checkbox" />
                                <div className="button-switch"></div>
                            </div><span className="stroke-separator"></span>
                                <label htmlFor="branch-sweep-account" className="text">Branch Sweep Account
                                    <gds-tooltip>By default, all branches use the same sweep account as their agency. However, you can add or remove a new sweep account exclusive to a branch.</gds-tooltip>
                                    <button type="button" className={`gds-link button-to-link button-to-link-enroll`} onClick={onClickAddOrDelete} disabled={isLoading} >
                                        {"Add"}
                                    </button>
                                    <p>{"Exclusive to this branch"}</p>
                                </label></>)
                        }
                        {(agencyProfile?.sweepDetails?.SweepsPaymentMethodId ?? null) !== null ?
                            <SweepDetailsContainer agencyProfile={agencyProfile} setAgencyBranches={setAgencyBranches} isBranch={true} resetTrigger={resetForms} onClickAddOrDelete={onClickAddOrDelete} isLoading={isLoading} />
                            : null
                        }
                        {(showAlert) &&
                            <div id="alert-low" className="alert">
                                <div className="alert--low-importance">
                                    <span className="icon-alert" />
                                    <ul className="alert-list">
                                        <li className="alert-content">
                                            <button aria-label="Close alert" className="icon-close" onClick={() => setShowAlert(false)}></button>
                                            <div className="h4">Inactive Agency Sweep Account</div>
                                            <p>
                                                This agency sweep account is Inactive. You can activate it by selecting the toggle switch above or by
                                                adding a branch sweep account.
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div id={`branch-deletion-modal-${agencyProfile?.agencyId}`} className="modal" >
                <div className="modal-container">
                    <h4 className="modal-headline">Unenroll this branch?</h4>
                    <div className="full-width-line" />
                    <div className="modal-content modal-content-with-cta-bar">
                        If you continue, this branch will be unenrolled from sweep payment services and all payment information will be removed.
                    </div>
                    <div className="modal-content">
                        <div className="modal--call-to-action-bar">
                            <button
                                type="button"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                onClick={handleDeleteClick}
                                disabled={isLoading}
                            >
                                <span>Unenroll Branch</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <button
                        type="button"
                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                        onClick={() => onCancel()}
                    >
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
        </form>
    );
}

const validationSchema = yup.object({
    isAdminDeactivated: yup.bool().required("Required.")
});